import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  LogOut,
  Menu,
  WifiOff,
  ChevronDown,
  UserCheck,
  Building,
  Mail,
  Hash,
  Shield,
  Coins,
} from 'lucide-react';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useShellState } from '@/app/context/ShellContext';
import { NotificationCenter } from '@/components/feedback/NotificationCenter';
import { ThemeToggle } from './ThemeToggle';
import { formatInr } from '@/lib/utils';
import { ROUTES } from '@/constants/routes';

export const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const {
    setIsCommandPaletteOpen,
    setIsMobileDrawerOpen,
    isOnline,
  } = useShellState();
  const navigate = useNavigate();

  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);
  const userMenuRef = useRef<HTMLDivElement>(null);

  // Close user menu on outside click
  useEffect(() => {
    const handleOutsideClick = (e: MouseEvent) => {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target as Node)) {
        setIsUserMenuOpen(false);
      }
    };
    if (isUserMenuOpen) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [isUserMenuOpen]);


  const userInitials = user?.name
    ? user.name
        .split(' ')
        .map((n) => n[0])
        .join('')
        .substring(0, 2)
        .toUpperCase()
    : 'AH';

  return (
    <header className="sticky top-0 z-50 flex h-16 w-full items-center justify-between border-b border-border bg-white/95 dark:bg-[#0c0d14]/95 px-4 sm:px-6 backdrop-blur-md shadow-xs font-heading">
      {/* Mobile Drawer Button & Search Trigger */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => setIsMobileDrawerOpen(true)}
          className="flex lg:hidden rounded-md p-2 hover:bg-muted text-foreground-secondary cursor-pointer"
          aria-label="Open mobile menu"
        >
          <Menu className="h-5 w-5" />
        </button>

        {/* Command Palette Search Trigger */}
        <button
          onClick={() => setIsCommandPaletteOpen(true)}
          className="flex items-center gap-2 rounded-xl border border-border bg-muted/40 px-3.5 py-1.5 text-xs text-foreground-muted hover:border-primary/50 hover:bg-muted transition-all cursor-pointer"
        >
          <Search className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Search orders, PRs, vendors, invoices...</span>
          <span className="sm:hidden">Search...</span>
          <kbd className="hidden lg:inline-flex items-center rounded border border-border bg-surface px-1.5 text-[10px] font-mono text-foreground-muted">
            Ctrl+K
          </kbd>
        </button>
      </div>

      {/* User Actions, Network Status, Theme, and Interactive User Profile */}
      <div className="flex items-center gap-3">
        {/* Offline Banner Pill */}
        {!isOnline && (
          <span className="hidden sm:inline-flex items-center gap-1 rounded bg-rose-500/10 text-rose-700 dark:text-rose-400 px-2 py-0.5 text-[10px] font-bold border border-rose-500/20">
            <WifiOff className="h-3 w-3" /> Offline Mode
          </span>
        )}

        {/* Theme Selector Toggle (Sun/Moon) */}
        <ThemeToggle />

        {/* Notification Center Popover */}
        <NotificationCenter />

        {/* Interactive User Profile Trigger */}
        <div className="relative pl-2 border-l border-border" ref={userMenuRef}>
          <button
            type="button"
            onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
            className="flex items-center gap-2.5 px-2 py-1.5 rounded-2xl hover:bg-muted/70 transition-all text-left focus:outline-none focus:ring-2 focus:ring-primary/40 cursor-pointer group border border-transparent hover:border-border"
            aria-expanded={isUserMenuOpen}
            aria-label="Toggle user details menu"
          >
            {/* Prominent, Crisp Initial Icon Badge */}
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-gradient-to-tr from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-black text-xs shadow-sm ring-2 ring-primary/25 group-hover:scale-105 transition-transform">
              {userInitials}
            </div>

            <div className="hidden sm:block text-left pr-1">
              <p className="text-xs font-bold text-foreground leading-tight group-hover:text-primary transition-colors">
                {user?.name || 'Alexander Hamilton'}
              </p>
              <p className="text-[10px] font-semibold text-foreground-muted">
                {user?.role || 'ADMIN'}
              </p>
            </div>

            <ChevronDown
              className={`h-4 w-4 text-foreground-muted transition-transform duration-200 ${
                isUserMenuOpen ? 'rotate-180 text-primary' : ''
              }`}
            />
          </button>

          {/* User Details Dropdown Card */}
          {isUserMenuOpen && (
            <div className="absolute right-0 mt-2.5 w-84 sm:w-92 rounded-3xl border border-border bg-card p-5 shadow-2xl ring-1 ring-black/10 dark:ring-white/10 z-[100] animate-in fade-in slide-in-from-top-2 duration-150 space-y-3.5 font-heading">
              {/* Profile Card Header */}
              <div className="flex items-start gap-3.5 pb-3 border-b border-border/80">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-tr from-[#3d5ad6] via-[#4767f0] to-[#798fec] text-white font-black text-base shadow-md ring-2 ring-primary/30">
                  {userInitials}
                </div>

                <div className="flex-1 min-w-0">
                  <h4 className="text-sm font-black text-foreground truncate">
                    {user?.name || 'Alexander Hamilton'}
                  </h4>
                  <p className="text-xs text-foreground-muted truncate flex items-center gap-1.5 mt-0.5">
                    <Mail className="h-3 w-3 shrink-0 text-primary" />
                    <span className="truncate">{user?.email || 'admin@procure.com'}</span>
                  </p>
                  <span className="inline-flex items-center gap-1 mt-1.5 px-2 py-0.5 rounded-lg bg-primary/10 text-primary text-[10px] font-black uppercase tracking-wider">
                    <UserCheck className="h-3 w-3" />
                    {user?.role || 'ADMIN'}
                  </span>
                </div>
              </div>

              {/* Organization & Session Meta */}
              <div className="space-y-2 text-xs">
                <div className="flex items-center justify-between text-foreground-secondary">
                  <span className="flex items-center gap-1.5 font-semibold text-[11px]">
                    <Hash className="h-3.5 w-3.5 text-primary" /> Employee ID
                  </span>
                  <span className="font-mono font-bold text-foreground">
                    {user?.employeeId || user?.id || 'EMP-9009'}
                  </span>
                </div>

                <div className="flex items-center justify-between text-foreground-secondary">
                  <span className="flex items-center gap-1.5 font-semibold text-[11px]">
                    <Building className="h-3.5 w-3.5 text-primary" /> Department
                  </span>
                  <span className="font-bold text-foreground truncate max-w-[170px]">
                    {user?.departmentName || 'Information Technology'}
                  </span>
                </div>

                <div className="flex items-center justify-between text-foreground-secondary">
                  <span className="flex items-center gap-1.5 font-semibold text-[11px]">
                    <Shield className="h-3.5 w-3.5 text-primary" /> Scope Ownership
                  </span>
                  <span className="font-bold text-primary uppercase text-[11px] bg-primary/10 px-2 py-0.5 rounded-md">
                    {user?.accessScope?.ownership || 'ENTERPRISE'}
                  </span>
                </div>

                <div className="flex items-center justify-between text-foreground-secondary">
                  <span className="flex items-center gap-1.5 font-semibold text-[11px]">
                    <Coins className="h-3.5 w-3.5 text-primary" /> DOA Authority Limit
                  </span>
                  <span className="font-bold font-mono text-emerald-600 dark:text-emerald-400">
                    {user?.role === 'ADMIN' ? 'Unlimited' : formatInr(user?.approvalAuthorityLimit || 0)}
                  </span>
                </div>
              </div>


              {/* Sign Out Action Button */}
              <div className="pt-2 border-t border-border/80">
                <button
                  type="button"
                  onClick={() => {
                    setIsUserMenuOpen(false);
                    logout();
                    navigate(ROUTES.AUTH.LOGIN);
                  }}
                  className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-xl text-xs font-bold text-rose-600 dark:text-rose-400 hover:bg-rose-500/10 transition-colors cursor-pointer"
                >
                  <LogOut className="h-3.5 w-3.5" /> Sign Out of ProcureAI
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
};
